import React from "react"
import PropTypes from "prop-types"
import { Link, graphql } from "gatsby"
import Img from "gatsby-image"
import { Global, css } from "@emotion/core"
import styled from "@emotion/styled"
import { MDXProvider } from "@mdx-js/react"
import { MDXRenderer } from "gatsby-plugin-mdx"

// My components

import { breakPoints, gutter, toEm, toRem, grid, isEmpty, letterSpacing } from "../styles/utils"
import Container from "./container"
import Header from "./header"
import Footer from "./footer"
import Wrapper from "./wrapper"
import SEO from "./seo"
import Block from "./block"

const BannerWrapper = styled.div`
  background-color: var(--current-bg-color);
  position: relative;
  overflow: hidden;
  margin-bottom: ${toRem(40)};
  @media (min-width: ${breakPoints.large}) {
    margin-bottom: ${toRem(70)};
  }
`

const Info = styled.div`
  position: absolute;
  bottom: ${toRem(20)};
  width: 100%;
  z-index: 9;
  @media (min-width: ${breakPoints.medium}) {
    transform: translateY(-50%);
    top: 50%;
    bottom: auto;
  }
`

const InfoContainer = styled(Container)`
  justify-content: left;
`

const Copy = styled.div`
  ${gutter};
  width: 100%;
  @media (min-width: ${breakPoints.small}) {
    width: ${grid(5, 12)};
  }
`

const WorkTitle = styled.h1`
  --title-font-size: ${toEm(23)};
  --title-color: var(--current-text-color);
  line-height: 1;
  margin-bottom: ${toRem(4)};

  @media (min-width: ${breakPoints.small}) and (max-width: ${breakPoints.medium}) {
    --title-font-size: ${toEm(26)};
  }
  @media (min-width: ${breakPoints.medium}) and (max-width: ${breakPoints.large}) {
    --title-font-size: ${toEm(36)};
  }
  @media (min-width: ${breakPoints.large}) {
    --title-font-size: ${toEm(50)};
  }
  @media (min-width: ${breakPoints.jumbo}) {
    --title-font-size: ${toEm(75)};
    letter-spacing: ${letterSpacing(1)};
  }
`

const WorkTags = styled.p`
  color: var(--current-text-color);
  font-weight: 400;
  margin-bottom: 0;
  text-transform: capitalize;
  opacity: 0.75;

  @media (min-width: ${breakPoints.medium}) {
    font-size: ${toEm(20)};
    margin-top: ${toRem(10)};
  }
`

const ImageWrapper = styled.div`
  background-color: var(--current-bg-color);
  z-index: 1;
  top: 0;

  .hero-large {
    display: none;
  }

  @media (min-width: ${breakPoints.small}) and (max-width: ${breakPoints.medium}) {
    .hero-small {
      display: none;
    }

    .hero-large {
      display: block;
    }
  }
  @media (min-width: ${breakPoints.medium}) {
    .hero-small {
      display: none;
    }

    .hero-large {
      display: block;
    }
  }
`

const PagerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: ${toRem(30)};
  margin-bottom: ${toRem(60)};
`

const pagerStyles = css`
  flex: 1 0 0;
  overflow: hidden;
`

const pagerLinkStyles = css`
  a {
    color: inherit;
    position: relative;
    line-height: 1.2;
    display: block;
  }
`

const PagerRadius = styled.div`
  ${pagerStyles};
  overflow: hidden;
  display: flex;
`

const PagerNext = styled.div`
  ${pagerStyles};
  ${pagerLinkStyles};
  background-color: var(--next-bg-color);
  color: var(--next-text-color);
`

const PagerPrev = styled.div`
  ${pagerStyles};
  ${pagerLinkStyles};
  background-color: var(--prev-bg-color);
  color: var(--prev-text-color);
  display: none;
  @media (min-width: ${breakPoints.medium}) {
    display: block;
  }
`

const PagerInfo = styled.div`
  position: absolute;
  top: ${toRem(28)};
  left: 0;
  z-index: 1;
  padding-right: ${toRem(20)};
  padding-left: ${toRem(20)};
  width: 91%;

  @media (min-width: ${breakPoints.medium}) {
    padding-right: ${toRem(30)};
    padding-left: ${toRem(30)};
  }
`

const PagerDirection = styled.span`
  color: inherit;
  font-size: ${toEm(14)};
  text-transform: uppercase;
  opacity: 0.7;
`

const PagerTitle = styled.h3`
  color: inherit;
  line-height: 1.2;
  width: 50%;
`

const ThankYou = styled.div`
  margin-top: ${toRem(10)};

  h2 {
    margin-bottom: ${toRem(5)};
  }

  p {
    margin-bottom: 0;
  }

  .previous-text {
    display: none;
  }

  @media (min-width: ${breakPoints.medium}) {
    text-align: center;
    .previous-text {
      display: inline-block;
    }
  }

  @media (min-width: ${breakPoints.large}) {
    margin-top: ${toRem(15)};
    margin-bottom: ${toRem(60)};
  }
`

const BackToWork = styled.div`
  position: sticky;
  direction: rtl;
  height: 0;
  bottom: 60px;
  z-index: 10;

  a {
    background-color: var(--link);
    display: block;
    height: ${toRem(45)};
    width: ${toRem(45)};
    margin-right: ${toRem(10)};
    border-radius: ${toRem(300)};
    overflow: hidden;
    box-shadow: 0px 5px 8px 3px rgba(0, 0, 0, 0.1);
    position: relative;
  }

  @media (min-width: ${breakPoints.medium}) {
    bottom: 80px;
    a {
      height: ${toRem(54)};
      width: ${toRem(54)};
      margin-right: ${toRem(20)};
    }
  }
`

const ArrowSvg = styled.svg`
  width: 100%;
  height: 100%;

  > path {
    fill: #fff;
  }
`

const Work = ({ data, pageContext, location }) => {
  const { viewImage, nextImage, prevImage } = data
  const { view, prev, next } = pageContext

  const nextData = {
    url: `${next.slug}`,
    title: next.frontmatter.title,
    textColor: next.frontmatter.dark ? `var(--white)` : `var(--gray-darker)`,
    bgColor: isEmpty(next.frontmatter.color) ? `var(--gray-lightest)` : next.frontmatter.color,
    pagerImg: nextImage.frontmatter.pagerImg.childImageSharp.fluid,
  }

  const prevData = {
    url: `${prev.slug}`,
    title: prev.frontmatter.title,
    textColor: prev.frontmatter.dark ? `var(--white)` : `var(--gray-darker)`,
    bgColor: isEmpty(prev.frontmatter.color) ? `var(--gray-lightest)` : prev.frontmatter.color,
    pagerImg: prevImage.frontmatter.pagerImg.childImageSharp.fluid,
  }

  const viewData = {
    url: `${view.slug}`,
    title: view.frontmatter.title,
    isDark: view.frontmatter.dark,
    textColor: view.frontmatter.dark ? `var(--white)` : `var(--gray-darker)`,
    bgColor: isEmpty(view.frontmatter.color) ? `var(--gray-lightest)` : view.frontmatter.color,
    link: isEmpty(view.frontmatter.link) ? `var(--link)` : view.frontmatter.link,
    excerpt: view.excerpt,
    body: view.body,
    tags: view.frontmatter.tags.sort().join(", "),
    heroImgLarge: viewImage.frontmatter.heroImgLarge.childImageSharp.fluid,
    heroImgSmall: viewImage.frontmatter.heroImgSmall.childImageSharp.fluid,
    seoImg: viewImage.frontmatter.seoImg.childImageSharp.resize.src,
    float: true,
  }

  return (
    <>
      <Global
        styles={css`
          #MainContent {
            --link: ${viewData.link};
            --current-bg-color: ${viewData.bgColor};
            --current-text-color: ${viewData.textColor};
            --prev-bg-color: ${prevData.bgColor};
            --prev-text-color: ${prevData.textColor};
            --next-bg-color: ${nextData.bgColor};
            --next-text-color: ${nextData.textColor};
          }
        `}
      />
      <Wrapper>
        <SEO
          title={viewData.title}
          description={viewData.excerpt}
          image={viewData.seoImg}
          pathname={location.pathname}
        />
        <Header float={viewData.float} dark={viewData.isDark} />
        <main id="MainContent">
          <BannerWrapper>
            <Info>
              <InfoContainer>
                <Copy>
                  <WorkTitle>{viewData.title}</WorkTitle>
                  <WorkTags>{viewData.tags}</WorkTags>
                </Copy>
              </InfoContainer>
            </Info>
            <ImageWrapper>
              {viewData.heroImgLarge && (
                <Img
                  alt={`${viewData.title} - Large hero image`}
                  backgroundColor={viewData.bgColor}
                  className="hero-large"
                  fluid={viewData.heroImgLarge}
                  placeholderClassName="gatsby-image-placeholder"
                />
              )}
              {viewData.heroImgSmall && (
                <Img
                  alt={`${viewData.title} - Small hero image`}
                  backgroundColor={viewData.bgColor}
                  className="hero-small"
                  fluid={viewData.heroImgSmall}
                  placeholderClassName="gatsby-image-placeholder"
                />
              )}
            </ImageWrapper>
          </BannerWrapper>

          <MDXProvider>
            <MDXRenderer>{viewData.body}</MDXRenderer>
            <Block>
              <ThankYou>
                <h2>Thank you for viewing!</h2>
                <p>
                  Interested to see more work? Navigate below or{" "}
                  <Link to="/#recent">go back here</Link>.
                </p>
              </ThankYou>
            </Block>
          </MDXProvider>

          <BackToWork>
            <Link to="/#recent" aria-label="Back to Recent Work">
              <ArrowSvg viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M24.036 34.0721L15 25.036L24.036 16L25.7793 17.7239L19.7262 23.777H34.9316V26.2951H19.7262L25.7793 32.3385L24.036 34.0721Z"
                  fill="none"
                />
              </ArrowSvg>
            </Link>
          </BackToWork>

          <PagerWrapper>
            <PagerRadius>
              {prevData && (
                <PagerPrev className="pager-prev">
                  <Link to={prevData.url}>
                    <PagerInfo>
                      <PagerDirection>Prev</PagerDirection>
                      <PagerTitle>{prevData.title}</PagerTitle>
                    </PagerInfo>
                    <Img
                      alt={`${prevData.title} - Work thumbnail`}
                      backgroundColor={prevData.bgColor}
                      fluid={prevData.pagerImg}
                      placeholderClassName="gatsby-image-placeholder"
                    />
                  </Link>
                </PagerPrev>
              )}
              {nextData && (
                <PagerNext className="pager-next">
                  <Link to={nextData.url}>
                    <PagerInfo>
                      <PagerDirection>Next</PagerDirection>
                      <PagerTitle>{nextData.title}</PagerTitle>
                    </PagerInfo>
                    <Img
                      alt={`${nextData.title} - Work thumbnail`}
                      backgroundColor={nextData.bgColor}
                      fluid={nextData.pagerImg}
                      placeholderClassName="gatsby-image-placeholder"
                    />
                  </Link>
                </PagerNext>
              )}
            </PagerRadius>
          </PagerWrapper>
        </main>
        <Footer />
      </Wrapper>
    </>
  )
}

export const pageQuery = graphql`
  query ProjectViewQuery($viewId: String, $nextId: String, $prevId: String) {
    viewImage: mdx(id: { eq: $viewId }) {
      frontmatter {
        heroImgSmall {
          ...imageSmallHeroProp
        }
        heroImgLarge {
          ...imageLargeHeroProp
        }
        seoImg {
          ...imageSEOProp
        }
      }
    }
    nextImage: mdx(id: { eq: $nextId }) {
      frontmatter {
        pagerImg {
          ...imagePagerProp
        }
      }
    }
    prevImage: mdx(id: { eq: $prevId }) {
      frontmatter {
        pagerImg {
          ...imagePagerProp
        }
      }
    }
  }
`

Work.propTypes = {
  data: PropTypes.shape().isRequired,
  pageContext: PropTypes.shape().isRequired,
  location: PropTypes.shape().isRequired,
}

export default Work
